// Considered composing existing list/grid organisms for the level layout:
// rejected because this canvas renders a directional node graph with
// ContextFlowConnector segments between nodes, which no existing organism provides.
import React, { useCallback } from 'react';
import { useTranslation } from 'localization';
import ContextFlowConnectorAtom from '../../atoms/ContextFlowConnector/ContextFlowConnectorAtom';
import ContextFlowNodeMolecule, {
  ContextFlowRootNodeMolecule,
} from '../../molecules/ContextFlowNode/ContextFlowNodeMolecule';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import type { ContextFlowLevel } from '../../../types/contextFlow';
import './ContextFlowCanvasOrganism.scss';

export interface ContextFlowCanvasOrganismProps {
  levels: ContextFlowLevel[];
  selectedId: string | null;
  onLevelSelect: (id: string) => void;
  onAddLevel: (direction: 'outwards' | 'inwards') => void;
  onRemoveLevel: (id: string) => void;
  onReorderLevels: (newOrder: ContextFlowLevel[]) => void;
  readOnly?: boolean;
  /**
   * EAINT-862 Phase 3: an agent-proposed continuation of the walk, rendered as dashed,
   * non-interactive nodes appended after the last real level — never interleaved with real
   * levels. Absent/empty → no overlay.
   */
  ghostLevels?: ContextFlowLevel[];
  /** Replaces the draft with the ghost proposal (root/levels/settings/name/description/tags) and marks it dirty — owned by the host, not this organism. */
  onAcceptGhost?: () => void;
  /** Discards the ghost proposal, leaving the draft untouched. */
  onDismissGhost?: () => void;
}

const ContextFlowCanvasOrganism: React.FC<ContextFlowCanvasOrganismProps> = ({
  levels,
  selectedId,
  onLevelSelect,
  onAddLevel,
  onRemoveLevel,
  onReorderLevels,
  readOnly = false,
  ghostLevels,
  onAcceptGhost,
  onDismissGhost,
}) => {
  const { t } = useTranslation();

  const handleMoveUp = useCallback(
    (id: string) => {
      const idx = levels.findIndex((l) => l.id === id);
      if (idx <= 0) return;
      const next = [...levels];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      onReorderLevels(next);
    },
    [levels, onReorderLevels]
  );

  const handleMoveDown = useCallback(
    (id: string) => {
      const idx = levels.findIndex((l) => l.id === id);
      if (idx < 0 || idx >= levels.length - 1) return;
      const next = [...levels];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      onReorderLevels(next);
    },
    [levels, onReorderLevels]
  );

  return (
    <div className="context-flow-canvas-organism">
      {/* Section header */}
      <div className="context-flow-canvas-organism__section-header">
        <div className="context-flow-canvas-organism__header-left">
          <span className="context-flow-canvas-organism__section-badge" aria-hidden="true">C</span>
          <div className="context-flow-canvas-organism__header-content">
            <span className="context-flow-canvas-organism__section-title">
              {t('context.editor.canvas.sectionTitle', 'The walk')}
            </span>
            <span className="context-flow-canvas-organism__section-sub">
              {t('context.editor.canvas.sectionSubtitle', 'Each level is a wave through the graph. Direction is per level — mix outwards and inwards as needed.')}
            </span>
          </div>
        </div>
        <div className="context-flow-canvas-organism__header-actions">
          <button type="button"
            className="context-flow-canvas-organism__header-btn"
            aria-label="Fit"
            title="Fit"
          >
            <i className="pi pi-search" aria-hidden="true" />{' '}
            Fit
          </button>
          <button type="button"
            className="context-flow-canvas-organism__header-btn"
            aria-label="Duplicate"
            title="Duplicate"
          >
            <i className="pi pi-copy" aria-hidden="true" />{' '}
            Duplicate
          </button>
        </div>
      </div>

      {/* Flow area */}
      <div className="context-flow-canvas-organism__grid-bg">
        <ul className="context-flow-canvas-organism__flow-row">
          {/* Root node */}
          <li>
            <ContextFlowRootNodeMolecule
              active={selectedId === 'root'}
              onClick={() => onLevelSelect('root')}
              data-testid="cf-node-root"
            />
          </li>

          {/* Level nodes */}
          {levels.map((lv, idx) => (
            <React.Fragment key={lv.id}>
              {/* Connector with optional flips badge */}
              <li className="context-flow-canvas-organism__connector-wrap" aria-hidden="true">
                <ContextFlowConnectorAtom direction={lv.direction} />
                {lv.direction === 'inwards' && (
                  <ChipAtom
                    label={t('context.editor.flow.flips')}
                    className="context-flow-canvas-organism__flips-badge"
                  />
                )}
              </li>

              {/* Level node + reorder controls */}
              <li className="context-flow-canvas-organism__node-wrap">
                {/* Move-up/down only when selected and not readOnly */}
                {!readOnly && selectedId === lv.id && idx > 0 && (
                  <button
                    type="button"
                    className="context-flow-canvas-organism__reorder-btn"
                    data-testid={`cf-move-up-${lv.id}`}
                    aria-label={t('context.editor.canvas.moveUp')}
                    onClick={() => handleMoveUp(lv.id)}
                  >
                    <i className="pi pi-arrow-left" aria-hidden="true" />
                  </button>
                )}
                <ContextFlowNodeMolecule
                  level={lv}
                  order={idx + 1}
                  active={selectedId === lv.id}
                  readOnly={readOnly}
                  onClick={() => onLevelSelect(lv.id)}
                  data-testid={`cf-node-${lv.id}`}
                />
                {!readOnly && selectedId === lv.id && idx < levels.length - 1 && (
                  <button
                    type="button"
                    className="context-flow-canvas-organism__reorder-btn"
                    data-testid={`cf-move-down-${lv.id}`}
                    aria-label={t('context.editor.canvas.moveDown')}
                    onClick={() => handleMoveDown(lv.id)}
                  >
                    <i className="pi pi-arrow-right" aria-hidden="true" />
                  </button>
                )}
              </li>
            </React.Fragment>
          ))}

          {/* Ghost walk (EAINT-862 Phase 3) — an agent-proposed continuation of the walk, always
              appended after the last real level (never interleaved). Non-interactive nodes plus
              one Accept/Dismiss strip of real buttons. */}
          {ghostLevels && ghostLevels.length > 0 && (
            <>
              {ghostLevels.map((lv, idx) => (
                <React.Fragment key={`cf-ghost-${lv.id}`}>
                  <li
                    className="context-flow-canvas-organism__connector-wrap context-flow-canvas-organism__connector-wrap--ghost"
                    aria-hidden="true"
                  >
                    <ContextFlowConnectorAtom direction={lv.direction} />
                  </li>
                  <li className="context-flow-canvas-organism__node-wrap context-flow-canvas-organism__node-wrap--ghost">
                    <ContextFlowNodeMolecule
                      level={lv}
                      order={levels.length + idx + 1}
                      ghost
                      data-testid={`cf-ghost-node-${lv.id}`}
                    />
                  </li>
                </React.Fragment>
              ))}
              <li className="context-flow-canvas-organism__ghost-actions" data-testid="cf-ghost-actions">
                <button
                  type="button"
                  className="context-flow-canvas-organism__ghost-accept-btn"
                  data-testid="cf-ghost-accept"
                  aria-label={t('context.editor.agent.acceptGhost')}
                  onClick={onAcceptGhost}
                >
                  <i className="pi pi-check" aria-hidden="true" />
                  {t('context.editor.agent.acceptGhost')}
                </button>
                <button
                  type="button"
                  className="context-flow-canvas-organism__ghost-dismiss-btn"
                  data-testid="cf-ghost-dismiss"
                  aria-label={t('context.editor.agent.dismissGhost')}
                  onClick={onDismissGhost}
                >
                  <i className="pi pi-times" aria-hidden="true" />
                  {t('context.editor.agent.dismissGhost')}
                </button>
              </li>
            </>
          )}

          {/* Add level */}
          {!readOnly && (
            <>
              <li className="context-flow-canvas-organism__connector-wrap context-flow-canvas-organism__connector-wrap--dashed" aria-hidden="true" />
              <li className="context-flow-canvas-organism__add-item">
                <button
                  type="button"
                  className="context-flow-canvas-organism__add-btn"
                  data-testid="cf-add-level-btn"
                  aria-label={t('context.editor.canvas.addLevel')}
                  onClick={() => onAddLevel('outwards')}
                >
                  <span className="context-flow-canvas-organism__add-icon" aria-hidden="true">
                    <i className="pi pi-plus" />
                  </span>
                  <span className="context-flow-canvas-organism__add-label">
                    {t('context.editor.canvas.addLevel')}
                  </span>
                  <span className="context-flow-canvas-organism__add-hint">
                    {t('context.editor.canvas.addLevelHint')}
                  </span>
                </button>
              </li>
            </>
          )}
        </ul>
      </div>

      {/* Stats strip */}
      <div className="context-flow-canvas-organism__stats">
        <span className="context-flow-canvas-organism__stats-dot" aria-hidden="true" />
        <span>{t('context.editor.canvas.statsPreview', 'Sample preview')}</span>
        <span>
          <strong>{levels.filter((l) => l.direction === 'outwards').length}</strong>{' '}
          {t('context.editor.canvas.outwardsCount', 'outwards levels')}
        </span>
        <span>
          <strong>{levels.filter((l) => l.direction === 'inwards').length}</strong>{' '}
          {t('context.editor.canvas.inwardsCount', 'inwards level')}
        </span>
        <span className="context-flow-canvas-organism__stats-hint">
          {t('context.editor.canvas.liveResultHint', 'Live result count via /rescuate')}
        </span>
        <ButtonAtom
          label={t('context.editor.canvas.openPreview', 'Open full preview')}
          icon="pi pi-external-link"
          iconPos="right"
          variant="secondary"
          size="small"
          className="context-flow-canvas-organism__stats-btn"
        />
      </div>
    </div>
  );
};

export default ContextFlowCanvasOrganism;
